import React from "react";

import { RefreshCw } from "lucide-react";

import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import SearchInput from "../../Components/Search/SearchInput";
import Table from "../../Components/Table";
import "./style.scss";

interface AdminTableContainerProps {
  columns: any[];
  data: any[];
  loading?: boolean;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showRefresh?: boolean;
  onRefresh?: () => void;
  showAddButton?: boolean;
  addButtonLabel?: string;
  addButtonIcon?: React.ReactNode;
  onAddClick?: () => void;
  showFilters?: boolean;
  filters?: {
    label: string;
    selected: string;
    options: { label: string; value: string }[];
    onSelect: (value: string) => void;
  }[];
  showPagination?: boolean;
  rowsPerPage?: string;
  rowsOptions?: { label: string; value: string }[];
  onRowsPerPageChange?: (value: string) => void;
  title?: string;
  subtitle?: string | React.ReactNode;
  pagination?: boolean;
  maxHeight?: string;
}

const AdminTableContainer: React.FC<AdminTableContainerProps> = ({
  columns,
  data,
  loading = false,
  showSearch = false,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  showRefresh = false,
  onRefresh,
  showAddButton = false,
  addButtonLabel = "Add",
  addButtonIcon,
  onAddClick,
  showFilters = false,
  filters = [],
  showPagination = false,
  rowsPerPage = "10",
  rowsOptions = [],
  onRowsPerPageChange,
  title = "",
  subtitle,
  maxHeight,
  pagination,
}) => {
  const hasActions =
    (showFilters && filters.length > 0) ||
    showSearch ||
    showRefresh ||
    showAddButton;

  return (
    <div className="admin-table-container">
      <div className="table-header">
        {(hasActions || title) && (
          <div className="table-actions">
            <div className="flex flex-col">
              {title && (
                <h1 className="text-lg font-medium text-gray-900">{title}</h1>
              )}
              {subtitle && (
                <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {showFilters &&
                filters.map((filter, index) => (
                  <DropdownSelector
                    key={index}
                    label={filter.label}
                    selected={filter.selected}
                    options={filter.options}
                    onSelect={filter.onSelect}
                    enableScroll={true}
                  />
                ))}

              {showRefresh && (
                <button
                  onClick={onRefresh}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-all duration-200"
                  title="Refresh"
                >
                  <RefreshCw strokeWidth={1.5} className="h-5 w-5" />
                </button>
              )}

              {showSearch && (
                <SearchInput
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={e => onSearchChange?.(e.target.value)}
                />
              )}

              {showAddButton && (
                <Button
                  variant="primary"
                  onClick={onAddClick}
                  icon={addButtonIcon}
                >
                  {addButtonLabel}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {showPagination && (
        <div className="table-info">
          <span className="showing-text">
            Showing 1 to {Math.min(parseInt(rowsPerPage), data.length)} of{" "}
            {data.length} entries
          </span>
          <div className="flex flex-wraps-per-page">
            <span>Rows per page:</span>
            <DropdownSelector
              label="Rows per page"
              selected={rowsPerPage}
              options={rowsOptions}
              onSelect={value => onRowsPerPageChange?.(value)}
            />
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <Table
          columns={columns}
          data={showPagination ? data.slice(0, parseInt(rowsPerPage)) : data}
          loading={loading}
          scrollable={maxHeight ? true : false}
          pagination={pagination}
          maxHeight={maxHeight}
        />
      </div>
    </div>
  );
};

export default AdminTableContainer;
